/**
 * What the app may send to usage analytics: a fixed list of event names and a
 * fixed list of property names. Values are ids, enum values, counts and
 * booleans only; never a name, an e-mail, a label or free text.
 *
 * To add an event: add its name here (and its properties to `UsageEventProps`)
 * and call `useUsage().track(name, props)`. Nothing outside these lists leaves
 * the browser (`usage/scrub.ts` enforces it again just before sending).
 */
export const USAGE_EVENTS = [
  // Brand scouting. No page calls these yet: they are the proposed first set.
  'brand_scouting_tab_opened',
  'brand_scouting_filter_applied',
  'brand_scouting_export_started',
  // The global search (header, or the dashboard's) returned its results: how many per
  // section, never the term.
  'header_search_used',
  // An export button was clicked: which export (and the file format when known).
  'export_clicked',
  // A tab of a brand or partner profile was opened.
  'profile_tab_opened',
] as const;

export type UsageEventName = (typeof USAGE_EVENTS)[number];

/**
 * The kind of record a detail page shows (`record_type`, with `record_id` the
 * record's numeric id from the route; usage/records.ts).
 */
export const RECORD_TYPES = [
  'brand',
  'partner',
  'contract',
  'activation',
  'activity',
  'user',
  'role',
  'email_template',
] as const;

export type RecordType = (typeof RECORD_TYPES)[number];

/** Which export a button starts (`export_type` of `export_clicked`). */
export const EXPORT_TYPES = [
  'activity_list',
  'property_list',
  'contract_full',
  'linked_activities',
  'brand_scouting',
  'comparison',
  'retrospective',
  'store_list',
  'outreach_history',
  'data_inventory',
] as const;

export type ExportType = (typeof EXPORT_TYPES)[number];

/** The header search's sections, one count property each (`header_search_used`). */
export const SEARCH_SECTION_COUNTS = [
  'partner_count',
  'brand_count',
  'contract_count',
  'activity_count',
  'activation_count',
] as const;

export type SearchSectionCount = (typeof SEARCH_SECTION_COUNTS)[number];

export const USAGE_PROP_KEYS = [
  'brand_id',
  'partner_id',
  'tab',
  'source',
  'format',
  'count',
  'filter_count',
  'enabled',
  'record_type',
  'record_id',
  'export_type',
  ...SEARCH_SECTION_COUNTS,
] as const;

export type UsagePropKey = (typeof USAGE_PROP_KEYS)[number];
export type UsagePropValue = number | boolean | string;
export type UsageProps = Partial<Record<UsagePropKey, UsagePropValue>>;

/** The record of a detail page: its type and numeric id (the id of the route, nothing else). */
export interface UsageRecord {
  record_type: RecordType;
  record_id: number;
}

/** The properties each event takes (checked by the compiler for TypeScript callers). */
export interface UsageEventProps {
  brand_scouting_tab_opened: UsageProps;
  brand_scouting_filter_applied: UsageProps;
  brand_scouting_export_started: UsageProps;
  header_search_used: Partial<Record<SearchSectionCount, number>> & {
    source?: 'header' | 'dashboard';
  };
  export_clicked: { export_type: ExportType; format?: string } & Partial<UsageRecord>;
  profile_tab_opened: { record_type: 'brand' | 'partner'; record_id?: number; tab: string };
}

export const isUsageEventName = (name: unknown): name is UsageEventName =>
  typeof name === 'string' && (USAGE_EVENTS as readonly string[]).includes(name);

/** An enum value or an id written as text: one short token, no spaces, no `@`. */
const TOKEN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,47}$/;

const isUsageValue = (value: unknown): value is UsagePropValue =>
  typeof value === 'boolean' ||
  (typeof value === 'number' && Number.isFinite(value)) ||
  (typeof value === 'string' && TOKEN.test(value));

const oneOf =
  (list: readonly string[]) =>
  (value: unknown): boolean =>
    typeof value === 'string' && list.includes(value);

/** A record id: a positive whole number (never text, so never a name). */
export const isRecordId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

const isCount = (value: unknown): boolean =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

/** Keys with a stricter rule than `isUsageValue`: only these values pass. */
const STRICT: Partial<Record<UsagePropKey, typeof isCount>> = {
  record_type: oneOf(RECORD_TYPES),
  record_id: isRecordId,
  export_type: oneOf(EXPORT_TYPES),
  partner_count: isCount,
  brand_count: isCount,
  contract_count: isCount,
  activity_count: isCount,
  activation_count: isCount,
};

/** `props` cut down to the allowed keys with an allowed value (the rest is dropped). */
export const cleanUsageProps = (props: unknown): UsageProps => {
  const clean: UsageProps = {};
  if (!props || typeof props !== 'object') return clean;
  for (const key of USAGE_PROP_KEYS) {
    const value = (props as Record<string, unknown>)[key];
    const allowed = STRICT[key] ?? isUsageValue;
    if (allowed(value)) clean[key] = value as UsagePropValue;
  }
  return clean;
};
